import { computed, onUnmounted, ref, watch } from 'vue'
import { useStore } from 'vuex'

const ICON_HREF = '/favicon.png'
const ICON_SIZE = 64
const BADGE_COLOR = '#8844cc'
const BLINK_INTERVAL = 900

const baseTitle = document.title
const SOUND_KEY = 'unread_sound'
// Shared across the app: the header toggles it, the alerts read it
const soundEnabled = ref(localStorage.getItem(SOUND_KEY) !== 'off')

export function useSoundSetting() {
  const toggleSound = () => {
    soundEnabled.value = !soundEnabled.value
    localStorage.setItem(SOUND_KEY, soundEnabled.value ? 'on' : 'off')
  }

  return { soundEnabled, toggleSound }
}

let audioContext = null
let badgedIcon = null
let blinkTimer = null
let blinkOn = false

// A short two-tone chime, generated on the fly so no sound file is shipped
const playChime = () => {
  const Context = window.AudioContext || window.webkitAudioContext
  if (!Context) return

  audioContext = audioContext || new Context()
  // Browsers keep the context suspended until the operator interacts
  if (audioContext.state === 'suspended') audioContext.resume()

  const start = audioContext.currentTime
  ;[880, 1320].forEach((frequency, index) => {
    const oscillator = audioContext.createOscillator()
    const gain = audioContext.createGain()
    const at = start + index * 0.14

    oscillator.frequency.value = frequency
    gain.gain.setValueAtTime(0.0001, at)
    gain.gain.exponentialRampToValueAtTime(0.12, at + 0.02)
    gain.gain.exponentialRampToValueAtTime(0.0001, at + 0.13)
    oscillator.connect(gain).connect(audioContext.destination)
    oscillator.start(at)
    oscillator.stop(at + 0.14)
  })
}

const iconLink = () => document.querySelector('link[rel="icon"]')

// Painted whole, not stamped over the logo: a background tab decodes no images
const drawBadgedIcon = count => {
  const canvas = document.createElement('canvas')
  canvas.width = ICON_SIZE
  canvas.height = ICON_SIZE
  const context = canvas.getContext('2d')
  const center = ICON_SIZE / 2

  context.fillStyle = BADGE_COLOR
  context.beginPath()
  context.arc(center, center, center, 0, Math.PI * 2)
  context.fill()

  const label = count > 99 ? '99+' : String(count)
  context.fillStyle = '#ffffff'
  context.font = `bold ${label.length > 2 ? 26 : 40}px sans-serif`
  context.textAlign = 'center'
  context.textBaseline = 'middle'
  context.fillText(label, center, center + 2)

  return canvas.toDataURL('image/png')
}

const stopBlink = () => {
  clearInterval(blinkTimer)
  blinkTimer = null
  blinkOn = false
  const link = iconLink()
  if (link) link.href = ICON_HREF
}

const startBlink = () => {
  if (blinkTimer) return

  blinkTimer = setInterval(() => {
    const link = iconLink()
    if (!link || !badgedIcon) return

    blinkOn = !blinkOn
    link.href = blinkOn ? badgedIcon : ICON_HREF
  }, BLINK_INTERVAL)
}

// Unread threads are announced in the tab itself: a chime, a count in the
// title and a blinking icon while the operator looks elsewhere.
export function useUnreadAlerts() {
  const store = useStore()
  const unreadByConversation = computed(
    () => store.getters['stats/getUnreadByConversation']
  )
  let seenCounts = null

  const refreshTab = total => {
    document.title = total ? `(${total}) ${baseTitle}` : baseTitle
    if (!total) {
      badgedIcon = null
      stopBlink()
      return
    }

    badgedIcon = drawBadgedIcon(total)
    if (document.hidden) startBlink()
  }

  const onVisibilityChange = () => {
    if (!document.hidden) {
      stopBlink()
      return
    }
    // Unread threads that were already waiting start blinking as soon as
    // the operator switches away
    if (badgedIcon) startBlink()
  }

  watch(unreadByConversation, counts => {
    // Comparing totals missed a message that arrived while another thread was
    // being read, so every conversation is compared on its own
    const openId = String(store.getters['conversations/getSelectedId'] || '')
    const arrived = Object.entries(counts).some(
      ([id, count]) =>
        count > (seenCounts?.[id] || 0) &&
        // The operator is reading that thread and sees the message anyway,
        // unless they have switched to another tab
        (document.hidden || id !== openId)
    )
    // The first reading is what was already waiting, not something new
    if (seenCounts !== null && arrived && soundEnabled.value) playChime()

    seenCounts = counts
    refreshTab(Object.values(counts).reduce((total, count) => total + count, 0))
  })

  document.addEventListener('visibilitychange', onVisibilityChange)

  onUnmounted(() => {
    document.removeEventListener('visibilitychange', onVisibilityChange)
    stopBlink()
    document.title = baseTitle
  })
}
